/**
 * NotificationConfigEditor (Module C) — per-org delivery channel config.
 * In-app is always on. Here you enable Email and/or Telegram and set their
 * minimum level. Org-wide notifications (e.g. planner alarms) fan out to all
 * org members by email; Telegram goes to the org's configured chat.
 */
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import toast from 'react-hot-toast';
import { organizationsApi } from '../../services/api';
import { LoadingSpinner } from '../common';

type Level = 'info' | 'warning' | 'critical';
const selClass = 'rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

function Toggle({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-vida-600' : 'bg-gray-200'} ${disabled ? 'opacity-50' : ''}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

/**
 * Absolute base of the public API. Dashboard and API share the same public
 * origin via reverse proxy here, so building the URL against window.location
 * works reliably and avoids reading the Vite injected variable at runtime —
 * the bracketed form is undefined in production builds and would crash on use.
 */
function publicApiBase(): string {
  return `${window.location.origin}/api`;
}

export interface NotificationConfigEditorHandle {
  save: () => Promise<boolean>;
  isSaving: () => boolean;
}

const NotificationConfigEditor = forwardRef<NotificationConfigEditorHandle, { orgId: string; slug?: string }>(function NotificationConfigEditor({ orgId, slug }, ref) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailLevel, setEmailLevel] = useState<Level>('warning');
  const [tgEnabled, setTgEnabled] = useState(false);
  const [tgLevel, setTgLevel] = useState<Level>('warning');
  const [tgChatId, setTgChatId] = useState('');
  const [tgToken, setTgToken] = useState('');
  const [tgTokenSet, setTgTokenSet] = useState(false);
  const [waEnabled, setWaEnabled] = useState(false);
  const [waLevel, setWaLevel] = useState<Level>('warning');
  const [waPhoneId, setWaPhoneId] = useState('');
  const [waToken, setWaToken] = useState('');
  const [waTokenSet, setWaTokenSet] = useState(false);
  const [waRecipients, setWaRecipients] = useState('');
  const [waUseTemplate, setWaUseTemplate] = useState(false);
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waTemplateLang, setWaTemplateLang] = useState('es');
  const [wpEnabled, setWpEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    organizationsApi.getNotificationConfig(orgId)
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.data || {};
        setEmailEnabled(!!d.email?.enabled);
        setEmailLevel((d.email?.minLevel as Level) || 'warning');
        setTgEnabled(!!d.telegram?.enabled);
        setTgLevel((d.telegram?.minLevel as Level) || 'warning');
        setTgChatId(d.telegram?.chatId || '');
        setTgTokenSet(!!d.telegram?.botTokenSet);
        setWaEnabled(!!d.whatsapp?.enabled);
        setWaLevel((d.whatsapp?.minLevel as Level) || 'warning');
        setWaPhoneId(d.whatsapp?.phoneNumberId || '');
        setWaTokenSet(!!d.whatsapp?.accessTokenSet);
        setWaRecipients((d.whatsapp?.recipients || []).join(', '));
        setWaUseTemplate(!!d.whatsapp?.useTemplate);
        setWaTemplateName(d.whatsapp?.templateName || '');
        setWaTemplateLang(d.whatsapp?.templateLang || 'es');
        setWpEnabled(!!d.webpublish?.enabled);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [orgId]);

  const save = async (): Promise<boolean> => {
    setSaving(true);
    try {
      await organizationsApi.saveNotificationConfig(orgId, {
        email: { enabled: emailEnabled, minLevel: emailLevel },
        telegram: {
          enabled: tgEnabled,
          minLevel: tgLevel,
          chatId: tgChatId.trim(),
          ...(tgToken.trim() ? { botToken: tgToken.trim() } : {}),
        },
        whatsapp: {
          enabled: waEnabled,
          minLevel: waLevel,
          phoneNumberId: waPhoneId.trim(),
          recipients: waRecipients.split(',').map((s) => s.trim()).filter(Boolean),
          useTemplate: waUseTemplate,
          templateName: waTemplateName.trim(),
          templateLang: waTemplateLang.trim() || 'es',
          ...(waToken.trim() ? { accessToken: waToken.trim() } : {}),
        },
        webpublish: { enabled: wpEnabled },
      });
      if (tgToken.trim()) { setTgTokenSet(true); setTgToken(''); }
      if (waToken.trim()) { setWaTokenSet(true); setWaToken(''); }
      toast.success('Notificaciones actualizadas');
      return true;
    } catch {
      toast.error('Error al guardar notificaciones');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Expose save() al padre para que el boton "Guardar" del footer del modal
  // pueda dispararla. Se mantiene un boton local visible al final del editor
  // (al lado del de Cerrar arriba el patron no se respetaba).
  useImperativeHandle(ref, () => ({ save, isSaving: () => saving }), [save, saving]);

  if (loading) {
    return <div className="flex items-center justify-center py-4 text-gray-400"><LoadingSpinner size="sm" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="label">Notificaciones (canales)</label>
        {saving && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><LoadingSpinner size="sm" /> Guardando…</span>}
      </div>

      {/* Email */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Email</span>
          <Toggle on={emailEnabled} onClick={() => setEmailEnabled((v) => !v)} disabled={saving} />
        </div>
        {emailEnabled && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Nivel mínimo:</span>
            <select className={selClass} value={emailLevel} onChange={(e) => setEmailLevel(e.target.value as Level)}>
              <option value="info">Info (todas)</option>
              <option value="warning">Aviso</option>
              <option value="critical">Crítico</option>
            </select>
          </div>
        )}
      </div>

      {/* Telegram */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Telegram</span>
          <Toggle on={tgEnabled} onClick={() => setTgEnabled((v) => !v)} disabled={saving} />
        </div>
        {tgEnabled && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Nivel mínimo:</span>
              <select className={selClass} value={tgLevel} onChange={(e) => setTgLevel(e.target.value as Level)}>
                <option value="info">Info (todas)</option>
                <option value="warning">Aviso</option>
                <option value="critical">Crítico</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Chat ID</label>
                <input className={inputClass} value={tgChatId} onChange={(e) => setTgChatId(e.target.value)} placeholder="-1001234567890" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Bot token {tgTokenSet && <span className="text-green-600">(configurado)</span>}</label>
                <input type="password" className={inputClass} value={tgToken} onChange={(e) => setTgToken(e.target.value)} placeholder={tgTokenSet ? '•••••• (dejar vacío para mantener)' : '123456:ABC...'} autoComplete="off" />
              </div>
            </div>
            <p className="text-xs text-gray-400">Crea un bot con @BotFather, añádelo al chat/grupo y pon aquí su token y el chat ID.</p>
          </div>
        )}
      </div>

      {/* WhatsApp */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">WhatsApp</span>
          <Toggle on={waEnabled} onClick={() => setWaEnabled((v) => !v)} disabled={saving} />
        </div>
        {waEnabled && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Nivel mínimo:</span>
              <select className={selClass} value={waLevel} onChange={(e) => setWaLevel(e.target.value as Level)}>
                <option value="info">Info (todas)</option>
                <option value="warning">Aviso</option>
                <option value="critical">Crítico</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Phone Number ID</label>
                <input className={inputClass} value={waPhoneId} onChange={(e) => setWaPhoneId(e.target.value)} placeholder="123456789012345" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Access token {waTokenSet && <span className="text-green-600">(configurado)</span>}</label>
                <input type="password" className={inputClass} value={waToken} onChange={(e) => setWaToken(e.target.value)} placeholder={waTokenSet ? '•••••• (dejar vacío para mantener)' : 'EAAG...'} autoComplete="off" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Destinatarios (teléfonos, separados por comas)</label>
              <input className={inputClass} value={waRecipients} onChange={(e) => setWaRecipients(e.target.value)} placeholder="+34600111222, +34600333444" />
            </div>

            {/* Approved template (Meta) */}
            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Usar plantilla aprobada</span>
                <Toggle on={waUseTemplate} onClick={() => setWaUseTemplate((v) => !v)} disabled={saving} />
              </div>
              {waUseTemplate && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Nombre de la plantilla</label>
                    <input className={inputClass} value={waTemplateName} onChange={(e) => setWaTemplateName(e.target.value)} placeholder="aviso_generico" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Idioma</label>
                    <input className={inputClass} value={waTemplateLang} onChange={(e) => setWaTemplateLang(e.target.value)} placeholder="es" />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400">
                La plantilla debe estar <strong>aprobada en Meta</strong> y tener <strong>un único parámetro <code>{'{{1}}'}</code></strong> en el cuerpo:
                ahí se inserta el texto del aviso. Permite enviar <strong>fuera de la ventana de 24h</strong>.
              </p>
            </div>

            <p className="text-xs text-gray-400">
              WhatsApp Cloud API (Meta): pon el Phone Number ID y un access token. Ojo: fuera de la ventana de 24h, Meta exige
              <strong> plantillas aprobadas</strong>; los mensajes de texto solo llegan si el destinatario escribió primero.
            </p>
          </div>
        )}
      </div>

      {/* Web pública */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Publicación en web</span>
          <Toggle on={wpEnabled} onClick={() => setWpEnabled((v) => !v)} disabled={saving} />
        </div>
        <p className="text-xs text-gray-400">
          Expone los <strong>Anuncios</strong> (Marketing → Anuncios) en un endpoint público para que la web del org los muestre.
          Solo se publican los anuncios marcados como públicos; las notificaciones internas nunca salen.
        </p>
        {wpEnabled && slug && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Endpoint para tu web (GET)</label>
            <div className="flex gap-2">
              <input readOnly value={`${publicApiBase()}/public/marketing/${slug}/announcements`}
                className={`${inputClass} font-mono text-xs bg-gray-50`} onFocus={(e) => e.currentTarget.select()} />
              <button type="button" onClick={() => { navigator.clipboard.writeText(`${publicApiBase()}/public/marketing/${slug}/announcements`); toast.success('Endpoint copiado'); }}
                className="shrink-0 px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Copiar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default NotificationConfigEditor;
